import { ChangeEvent, FocusEvent } from "react";
import {
  Box,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { FormikErrors, FormikTouched } from "formik";
import { ControlledSelect, makeSxStyles } from "ui";
import { serviceTypeSelectOptions } from "service/types/enum/service-type-enum";
import FieldRow from "@/shared/components/FieldRow";
import { useNumericFields } from "@/shared/hooks/useNumericFields";
import { FormikSetters } from "@/shared/types/formik";
import { numericFieldSx } from "@/shared/utils/numeric-input";
import BillingAddressAutocomplete from "./BillingAddressAutocomplete";

// The customer-form "Finances" section groups the existing Service Type with the
// CRM contract price / billingAddress / financial notes. Rendered by the page
// (not CrmDetailsFields) because Service Type is a location field the lead form
// lacks. NOTE: `billingAddress` here is the CRM field (LocationDetails), matching
// the lead form — distinct from the location-level `billingInformation`, which
// the page keeps in its Address Info section.
export interface CustomerFinancesValues {
  type?: "onDemand" | "annual";
  contractPrice?: number;
  billingAddress?: string;
  financialNotes?: string;
}

interface CustomerFinancesSectionProps extends FormikSetters {
  values: CustomerFinancesValues;
  errors: FormikErrors<CustomerFinancesValues>;
  touched: FormikTouched<CustomerFinancesValues>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
}

const CustomerFinancesSection = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
}: CustomerFinancesSectionProps) => {
  const styles = useStyles();

  const numeric = useNumericFields({
    setFieldValue,
    setFieldTouched,
    handleBlur,
  });

  const price = numeric.field({
    name: "contractPrice",
    value: values.contractPrice,
    error: errors.contractPrice,
    touched: touched.contractPrice,
    allowDecimal: true,
  });

  return (
    <Box>
      <Divider sx={styles.divider} />
      <Typography sx={styles.sectionTitle}>Finances</Typography>
      <FieldRow label="Contract Price">
        <TextField
          id="contractPrice"
          type="number"
          value={price.value}
          onChange={price.onChange}
          onFocus={price.onFocus}
          onBlur={price.onBlur}
          error={price.showError}
          helperText={price.helperText}
          inputProps={price.inputProps}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={numericFieldSx}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Service Type">
        <ControlledSelect
          fullWidth
          placeholder="Service Type"
          options={serviceTypeSelectOptions}
          value={values.type}
          onChange={(value) => {
            setFieldValue("type", value);
            setFieldTouched("type", true);
          }}
        />
      </FieldRow>
      <FieldRow label="Billing Address">
        <BillingAddressAutocomplete
          value={values.billingAddress ?? ""}
          onChange={(v) => setFieldValue("billingAddress", v)}
          onBlur={handleBlur}
          error={touched.billingAddress && !!errors.billingAddress}
          helperText={touched.billingAddress && errors.billingAddress}
        />
      </FieldRow>
      <FieldRow label="Financial Notes">
        <TextField
          id="financialNotes"
          value={values.financialNotes ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.financialNotes && !!errors.financialNotes}
          helperText={touched.financialNotes && errors.financialNotes}
          fullWidth
          multiline
          minRows={3}
        />
      </FieldRow>
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  divider: {
    my: "24px",
  },
  sectionTitle: {
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
    mb: "20px",
  },
}));

export default CustomerFinancesSection;
